import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, FileText, Loader2, CheckCircle2, AlertTriangle,
  RotateCcw, Shield, X,
} from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_ICON = {
  csv_import:  FileSpreadsheet,
  xlsx_import: FileSpreadsheet,
  pdf_import:  FileText,
};

/**
 * ImportsPanel — upload CSV / XLSX / PDF, preview, commit, review history,
 * rollback (super admin only). Preview + commit are two separate round-trips
 * so the user always sees what's about to happen before anything is written.
 */
export const ImportsPanel = ({ lists, onReload, canRollback }) => {
  const [stage, setStage] = useState('idle');  // idle | parsing | preview | committing | done
  const [preview, setPreview] = useState(null);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [extraTags, setExtraTags] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const fileRef = React.useRef(null);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(`${API}/admin/contacts/imports`, { withCredentials: true });
      setHistory(data.batches || []);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const pickFile = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    if (!/\.(csv|xlsx|pdf)$/i.test(name)) {
      toast.error('Only CSV, XLSX or PDF files are supported.');
      return;
    }
    setStage('parsing');
    const form = new FormData();
    form.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/contacts/import/preview`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setPreview(data);
      setStage('preview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import preview failed');
      setStage('idle');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commit = async () => {
    if (!preview) return;
    setStage('committing');
    try {
      const { data } = await axios.post(`${API}/admin/contacts/import/commit`, {
        rows: preview.parsed_rows,
        source: preview.source,
        filename: preview.filename,
        list_ids: selectedListIds,
        tags: extraTags.split(',').map(t => t.trim()).filter(Boolean),
      }, { withCredentials: true, timeout: 300000 });
      toast.success(`Imported — created ${data.created}, updated ${data.updated}, skipped ${data.skipped}`);
      setStage('done');
      setPreview(null);
      setSelectedListIds([]);
      setExtraTags('');
      await loadHistory();
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import commit failed');
      setStage('preview');
    }
  };

  const rollback = async (batch) => {
    if (!window.confirm(`Roll back import "${batch.filename}"?\nThis will delete the ${batch.created} contact(s) created by this batch. Pre-existing contacts updated by it will NOT be deleted.`)) return;
    try {
      const { data } = await axios.post(`${API}/admin/contacts/imports/${batch.id}/rollback`, {}, { withCredentials: true });
      toast.success(`Rolled back — removed ${data.deleted} contacts`);
      await loadHistory();
      onReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rollback failed');
    }
  };

  return (
    <div data-testid="imports-panel">
      {/* Uploader */}
      <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-6 text-center mb-5">
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.pdf" onChange={handleFile} className="hidden" data-testid="imports-file-input" />
        <div className="w-10 h-10 mx-auto rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-2">
          {stage === 'parsing'
            ? <Loader2 className="w-4 h-4 animate-spin text-[#002249]" />
            : <Upload className="w-4 h-4 text-[#002249]" />}
        </div>
        <div className="font-semibold text-[14px] text-[#1a1a1a]">
          {stage === 'parsing' ? 'Parsing file…' : 'Upload CSV, XLSX or PDF'}
        </div>
        <div className="text-[11.5px] text-slate-500 mt-1">
          Up to 25 MB. PDF emails are extracted without inventing names.
        </div>
        <Button onClick={pickFile} disabled={stage === 'parsing' || stage === 'committing'} className="bg-[#002249] hover:bg-[#0a3570] mt-3" data-testid="imports-choose-file">
          Choose file
        </Button>
      </div>

      {/* Preview */}
      {stage === 'preview' && preview && (
        <PreviewPanel
          preview={preview}
          lists={lists}
          selectedListIds={selectedListIds}
          setSelectedListIds={setSelectedListIds}
          extraTags={extraTags}
          setExtraTags={setExtraTags}
          onCancel={() => { setPreview(null); setStage('idle'); }}
          onCommit={commit}
          committing={stage === 'committing'}
        />
      )}

      {/* History */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[13px] font-bold text-[#1a1a1a]">Import history</h3>
          {loadingHistory && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="imports-history">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">When</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">File</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">By</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Created</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Updated</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Skipped</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Status</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">No imports yet.</td></tr>
                )}
                {history.map((b) => {
                  const Icon = SOURCE_ICON[b.source] || FileText;
                  const canRoll = canRollback && b.status === 'committed' && b.created > 0;
                  return (
                    <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`imports-row-${b.id}`}>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{new Date(b.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <Icon className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-mono text-[11.5px]">{b.filename}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{b.uploader_name || '—'}</td>
                      <td className="px-3 py-2 font-semibold text-emerald-700">{b.created}</td>
                      <td className="px-3 py-2 font-semibold text-[#002249]">{b.updated}</td>
                      <td className="px-3 py-2 text-slate-500">{b.skipped}</td>
                      <td className="px-3 py-2">
                        {b.status === 'rolled_back' ? (
                          <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">ROLLED BACK</span>
                        ) : (
                          <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">COMMITTED</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {canRoll && (
                          <button onClick={() => rollback(b)} className="p-1 rounded hover:bg-rose-50 text-rose-500" title="Roll back" data-testid={`imports-rollback-${b.id}`}>
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewPanel = ({ preview, lists, selectedListIds, setSelectedListIds, extraTags, setExtraTags, onCancel, onCommit, committing }) => {
  const c = preview.counts || {};
  const classified = preview.classified || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5" data-testid="import-preview">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-bold text-[14px] text-[#1a1a1a]">
            Import preview — <span className="font-mono text-[12.5px] text-slate-600">{preview.filename}</span>
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            {preview.source?.replace('_', ' ')} · {preview.total_detected} rows detected
          </div>
        </div>
        <button onClick={onCancel} className="p-1 rounded-md text-slate-500 hover:bg-slate-50"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-1.5 mb-4">
        <Tile k="Valid" v={preview.valid} tone="good" />
        <Tile k="Invalid" v={preview.invalid} tone="warn" />
        <Tile k="File dupes" v={preview.duplicates_in_file} />
        <Tile k="New" v={c.new} tone="good" />
        <Tile k="Update" v={c.update} />
        <Tile k="Suppressed" v={c.suppressed} tone="warn" />
        <Tile k="Member link" v={c.member_link} />
      </div>

      {(c.suppressed || 0) > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-[12px] text-amber-900 flex items-start gap-1.5 mb-3">
          <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>
            {c.suppressed} email(s) in this file are already unsubscribed or hard-suppressed. They will remain suppressed — importing will not reactivate them.
          </span>
        </div>
      )}

      {/* Row sample */}
      <div className="border border-slate-200 rounded-lg overflow-hidden mb-3 max-h-[280px] overflow-y-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-2 py-1.5 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Email</th>
              <th className="text-left px-2 py-1.5 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Name</th>
              <th className="text-left px-2 py-1.5 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Company</th>
              <th className="text-left px-2 py-1.5 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">Classification</th>
            </tr>
          </thead>
          <tbody>
            {classified.slice(0, 60).map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`import-preview-row-${i}`}>
                <td className="px-2 py-1 font-mono text-[11px]">{r.email}</td>
                <td className="px-2 py-1 text-slate-600">{[r.first_name, r.last_name].filter(Boolean).join(' ') || <span className="text-slate-300">—</span>}</td>
                <td className="px-2 py-1 text-slate-600">{r.company || '—'}</td>
                <td className="px-2 py-1">
                  <ClassBadge cls={r.classification} suppressed={r.is_suppressed} member={r.is_member} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Attach context */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">Add every imported contact to these lists</label>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {(lists || []).length === 0 && <span className="text-[11px] text-slate-400">No lists yet.</span>}
            {(lists || []).map((l) => {
              const active = selectedListIds.includes(l.id);
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setSelectedListIds(active ? selectedListIds.filter(x => x !== l.id) : [...selectedListIds, l.id])}
                  className={`px-2 py-1 rounded-md text-[11.5px] font-semibold ${active ? 'bg-[#002249] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase font-semibold tracking-wide text-slate-500">Extra tags (comma-separated)</label>
          <input
            value={extraTags}
            onChange={(e) => setExtraTags(e.target.value)}
            placeholder="orlando-2026, prospect"
            className="mt-1.5 w-full border border-slate-200 rounded-md px-2 py-1.5 text-[12.5px] focus:border-[#002249] focus:outline-none"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={committing}>Cancel</Button>
        <Button onClick={onCommit} disabled={committing || preview.valid === 0} className="bg-[#002249] hover:bg-[#0a3570]" data-testid="import-commit">
          {committing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
          Import {preview.valid} contact(s)
        </Button>
      </div>
    </div>
  );
};

const Tile = ({ k, v, tone }) => (
  <div className={`rounded-md px-2 py-1 border ${tone === 'good' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : tone === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
    <div className="text-[9.5px] uppercase tracking-wide font-semibold opacity-80">{k}</div>
    <div className="text-sm font-bold">{v ?? 0}</div>
  </div>
);

const ClassBadge = ({ cls, suppressed, member }) => (
  <div className="flex items-center gap-1 flex-wrap">
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
      cls === 'new' ? 'bg-emerald-100 text-emerald-700' :
      cls === 'update' ? 'bg-sky-100 text-sky-700' :
      'bg-slate-100 text-slate-500'
    }`}>{(cls || 'unchanged').toUpperCase()}</span>
    {suppressed && (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 inline-flex items-center gap-0.5">
        <Shield className="w-2.5 h-2.5" /> SUPPRESSED
      </span>
    )}
    {member && (
      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#002249] text-white">MEMBER</span>
    )}
  </div>
);

export default ImportsPanel;
